import type { ZoneDef } from '../../layout/zones.js';
import { resolveZoneHelp, type ResolverState } from './zoneHelpCopy.js';
import type { AnchorRect } from '../../components/Tooltip.js';

/** Minimal contract the controller needs from a tooltip. */
export interface TooltipLike {
  show(title: string, body: string, anchor: AnchorRect): void;
  hide(): void;
}

export interface TooltipControllerOptions {
  tooltip: TooltipLike;
  getState: () => ResolverState;
  /** Delay before showing on a fresh hover (cancels on early hoverEnd). */
  showDelayMs: number;
}

/**
 * Owns the show/hide lifecycle for the inline tooltip.
 *
 * Hover semantics:
 *  - hover -> schedule show after showDelayMs
 *  - hoverEnd before delay -> cancel pending show (nothing rendered)
 *  - hoverEnd after show -> hide immediately
 *  - hover B while showing for A -> swap content in place (no hide flicker, no re-delay)
 *
 * setEnabled(false) cancels any pending show and hides immediately. The
 * scene-level capability gate (matchMedia hover) and pref toggle both flow
 * through setEnabled.
 */
export class TooltipController {
  private readonly tooltip: TooltipLike;
  private readonly getState: () => ResolverState;
  private readonly showDelayMs: number;
  private enabled = true;
  private visible = false;
  private pendingTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(opts: TooltipControllerOptions) {
    this.tooltip = opts.tooltip;
    this.getState = opts.getState;
    this.showDelayMs = opts.showDelayMs;
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    if (!enabled) {
      this.cancelPending();
      if (this.visible) {
        this.tooltip.hide();
        this.visible = false;
      }
    }
  }

  onHover(def: ZoneDef, anchor: AnchorRect): void {
    if (!this.enabled) return;
    if (this.visible) {
      // Direct zone-to-zone transition: swap content in place.
      this.cancelPending();
      const help = resolveZoneHelp(def, this.getState());
      if (help === null) {
        // New target has no copy — keep the previous tooltip up rather than
        // hiding silently. This matches the rule that resolver-null = no-op.
        return;
      }
      this.tooltip.show(help.title, help.body, anchor);
      return;
    }
    this.cancelPending();
    this.pendingTimer = setTimeout(() => {
      this.pendingTimer = null;
      const help = resolveZoneHelp(def, this.getState());
      if (help === null) return;
      this.tooltip.show(help.title, help.body, anchor);
      this.visible = true;
    }, this.showDelayMs);
  }

  onHoverEnd(): void {
    this.cancelPending();
    if (this.visible) {
      this.tooltip.hide();
      this.visible = false;
    }
  }

  private cancelPending(): void {
    if (this.pendingTimer !== null) {
      clearTimeout(this.pendingTimer);
      this.pendingTimer = null;
    }
  }
}
